import { TextField } from '@warp-ds/react';
import { type Dispatch, type ReactNode, useMemo, useState } from 'react';

import { t } from 'shared/locale/texts';
import { useConfig } from '../../../config/ConfigContext';
import { useBreakpoints } from '../../../hooks/useBreakpoints';
import { usePriceLevels } from '../../../search/common/usePriceLevels';
import actions from '../../../search/flight/flightActions';
import type { flightDefaultState } from '../../../search/flight/flightDefaultState';
import type { TripType } from '../../../types';
import { formatISODateTime, getOneYearFromToday, type PriceLevelType } from '../../../util/calendar';
import { addMonths, endOfMonth, setDate, startOfMonth, subMonths } from '../../../util/datetime-fns';
import { BasicDatePicker } from '../basicDatePicker/BasicDatePicker';
import { ExplanationBox } from './ExplanationBox/ExplanationBox';
import styles from './MetaDatePickerWrapper.module.css';
import { PriceLevelDisplay } from './PriceLevelDisplay/PriceLevelDisplay';

type InvalidFieldMap = Partial<Record<keyof ValidationErrorMap, boolean>>;

type FlightAction = ReturnType<(typeof actions)[keyof typeof actions]>;

type ValidationErrorMap = Partial<{
    departureDate: string | null;
    returnDate: string | null;
    returnDateAfterDeparture: string | null;
    origin: string | null;
    destination: string | null;
    sameOriginDestination: string | null;
    origin2: string | null;
    destination2: string | null;
    sameOriginDestination2: string | null;
}>;

interface MetaDatePickerWrapperProps {
    startDate: Date;
    endDate?: Date;
    form: Partial<typeof flightDefaultState>;
    invalid?: InvalidFieldMap;
    validationErrors: ValidationErrorMap;
    dispatch: Dispatch<FlightAction>;
    isReturnPicker?: boolean;
    openJawNumber?: number | null;
}

export const MetaDatePickerWrapper = ({
    startDate,
    endDate,
    form,
    invalid,
    validationErrors,
    dispatch,
    isReturnPicker,
    openJawNumber = null,
}: MetaDatePickerWrapperProps) => {
    const [displayedDate, setDisplayedDate] = useState(new Date());

    const { flightApiUrl } = useConfig();
    const { origin, destination, departureDate, returnDate, tripType } = form;

    const isSmallScreen = useBreakpoints({ breakpoint: 640 });
    const isWarpSM = useBreakpoints({ breakpoint: 'sm' });

    const isOpenJaw = tripType === 'openjaw';
    const useRange = tripType === 'roundtrip';

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const handleDateChange = (dates: { startDate: Date | null; endDate?: Date | null }) => {
        if (tripType === 'roundtrip') {
            dispatch(actions.setDates({ startDate: dates.startDate, endDate: dates.endDate ?? null }));
        } else if (tripType === 'oneway') {
            dispatch(actions.setDates({ startDate: dates.startDate }));
        } else if (tripType === 'openjaw') {
            dispatch(actions.setDates({ startDate: dates.startDate, endDate: returnDate }));
        }
        if (tripType === 'openjaw' && isReturnPicker) {
            dispatch(
                actions.setDates({
                    startDate: departureDate,
                    endDate: dates.startDate,
                }),
            );
        }
    };

    const dynamicStartDate = useMemo(() => {
        if (isSmallScreen) {
            return startOfMonth(subMonths(1, displayedDate));
        }
        return setDate(15, subMonths(1, startOfMonth(displayedDate)));
    }, [displayedDate, isSmallScreen]);

    const dynamicEndDate = useMemo(() => {
        if (isSmallScreen) {
            return endOfMonth(addMonths(1, displayedDate));
        }
        return setDate(15, addMonths(2, displayedDate));
    }, [displayedDate, isSmallScreen]);

    const { data } = usePriceLevels({
        url: `${flightApiUrl}/lowfare/calendar`,
        tripType: tripType as TripType,
        origin: origin ? origin : undefined,
        destination: destination ? destination : undefined,
        startDate: dynamicStartDate,
        endDate: dynamicEndDate,
    });

    const { dateLevels, dateData } = useMemo(() => {
        if (!Array.isArray(data)) {
            return { dateLevels: {}, dateData: {} };
        }

        return data.reduce(
            (acc, { date, level }) => {
                acc.dateLevels[date] = level;
                acc.dateData[date] = <PriceLevelDisplay priceLevel={level as PriceLevelType} />;
                return acc;
            },
            {
                dateLevels: {} as Record<string, string>,
                dateData: {} as Record<string, ReactNode>,
            },
        );
    }, [data]);

    const formatDynamicClassName = (date: Date) => {
        const isoDate = formatISODateTime(date);
        const level = dateLevels?.[isoDate] || '';
        return level.toLowerCase();
    };

    const anchorErrorText = Array.from(
        new Set(
            [invalid?.departureDate && validationErrors.departureDate, invalid?.returnDate && validationErrors.returnDate].filter(Boolean),
        ),
    ).join('. ');

    if (useRange) {
        return (
            <BasicDatePicker
                className={styles.MetaDatePicker}
                modalFooter={<ExplanationBox />}
                dateData={dateData}
                dayClassName={formatDynamicClassName}
                onDateChange={handleDateChange}
                visibleMonths={2}
                useRange={true}
                startDate={startDate}
                endDate={endDate}
                setDisplayedDate={setDisplayedDate}
                upperDateLimit={getOneYearFromToday()}
                useAnchor={
                    isWarpSM
                        ? {
                              title: 'Når vil du reise?',
                              invalid: invalid?.departureDate || invalid?.returnDate,
                              helpText: anchorErrorText || undefined,
                          }
                        : undefined
                }>
                <TextField
                    id={`date-${tripType}-outbound`}
                    data-testid={`date-${tripType}-outbound`}
                    label={t('flight.outbound.label')}
                    invalid={invalid?.departureDate}
                    helpText={validationErrors.departureDate}
                />
                <TextField
                    id={`date-${tripType}-inbound`}
                    data-testid={`date-${tripType}-inbound`}
                    label={t('flight.inbound.label')}
                    invalid={invalid?.returnDate}
                    helpText={validationErrors.returnDate}
                />
            </BasicDatePicker>
        );
    }

    return (
        <BasicDatePicker
            className={styles.MetaDatePicker}
            modalFooter={!isOpenJaw && <ExplanationBox />}
            dateData={dateData}
            dayClassName={formatDynamicClassName}
            onDateChange={handleDateChange}
            visibleMonths={2}
            useRange={false}
            startDate={startDate}
            setDisplayedDate={setDisplayedDate}
            upperDateLimit={getOneYearFromToday()}
            disabledDateRanges={isReturnPicker && form.departureDate ? [{ start: today, end: form.departureDate }] : []}
            useAnchor={
                isWarpSM
                    ? {
                          title: 'Når vil du reise?',
                          invalid: invalid?.departureDate || invalid?.returnDate,
                          helpText: anchorErrorText || undefined,
                      }
                    : undefined
            }>
            <TextField
                id={`date-${tripType}-outbound${openJawNumber ? `-${openJawNumber}` : ''}`}
                data-testid={`date-${tripType}-outbound${openJawNumber ? `-${openJawNumber}` : ''}`}
                label={t('flight.outbound.label')}
                invalid={invalid?.departureDate}
                helpText={validationErrors.departureDate}
            />
        </BasicDatePicker>
    );
};
